'use client';

import { defaultFetcher, FSLocale, localeLink } from '@fshr/shared';
import useSWR from 'swr';
import { getConfig } from '@fshr/shared-react';
import { ForYouResponse } from './types';
import LoadingIndicator from '../loading-indicator';
import ErrorAlert from '../error-alert';
import Button from '../button';
import Icon from '../icon';
import Cookies from 'js-cookie';
import { translate } from '@micro-apps/env';
import { ConversationsCard } from './conversation-card';
import { GlobalStoreType } from '@fshr/stores';
import { RecommendationsListComponent } from './recommendations-list';
import { useTranslations } from '@fshr/context/i18n-context';
import { HighlightedRecommendationsComponent } from './highlighted-recommendation/';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { useRecoTracking } from './analytics/recoTracker';

type ForYouBodyProps = {
	locale: FSLocale;
	globalStore?: GlobalStoreType;
};

const ForYouErrorState = () => {
	const t = useTranslations();

	return (
		<div className="flex flex-col gap-150 px-400 py-600 justify-center items-center">
			<Icon icon="alert" className="w-[24px] h-[24px] text-[24px] text-red-700" />
			<div className="text-subtitle1">{translate('Content failed to load')}</div>
			<div className="text-caption text-center">
				{translate('Our apologies, we couldn’t load this page due to an unexpected error.')}
				<br />
				{translate('Please try again.')}
			</div>
			<Button
				className="w-[120px]"
				intent={'outline'}
				onClick={() => {
					const baseUrl = window.location.href.split('#')[0];
					window.location.href = baseUrl + '#for-you';
					window.location.reload();
				}}
			>
				{translate('Try again')}
			</Button>
		</div>
	);
};

export const ForYouBody = observer((props: ForYouBodyProps) => {
	const { locale, globalStore } = props;
	const { NEXT_PUBLIC_BFF_BASE_URL } = getConfig();
	const t = useTranslations();
	const { trackRecoVisit } = useRecoTracking();
	const hasTrackedView = useRef(false);

	const getForYouUrl = () => {
		const queryString = new URLSearchParams();

		if (globalStore?.currentProperty) {
			queryString.set('propertyOwsCode', globalStore.currentProperty);
		}
		if (globalStore?.preferredCurrency) {
			queryString.set('currencyCode', globalStore.preferredCurrency);
		}
		const bookingId = Cookies.get('bookingId');
		if (bookingId) {
			queryString.set('cartId', bookingId);
		}

		return localeLink(locale ?? 'en')(`${NEXT_PUBLIC_BFF_BASE_URL}/for-you/?${queryString}`);
	};

	const { data, error, isLoading } = useSWR<ForYouResponse>(getForYouUrl(), defaultFetcher, {
		revalidateIfStale: false,
		revalidateOnFocus: false,
		revalidateOnReconnect: false,
		errorRetryCount: 0,
	});

	useEffect(() => {
		if (hasTrackedView.current) return;
		if (!data || isLoading || error) return;

		hasTrackedView.current = true;
		trackRecoVisit('for_you');
	}, [data, isLoading, error, trackRecoVisit]);

	if (isLoading) {
		return <LoadingIndicator />;
	}

	if (error && !isLoading) {
		return <ForYouErrorState />;
	}

	if (!data) {
		return <ForYouErrorState />;
	}

	const { recommendations, bffError } = data;

	if (bffError) {
		return <ForYouErrorState />;
	}

	if (!recommendations) {
		return <ForYouErrorState />;
	}

	return (
		<main className="w-full">
			<ConversationsCard
				conversationCard={recommendations.conversationCard}
				globalStore={globalStore}
			/>
			<div className="w-full flex flex-col spacing-stack-l px-[20px] md:px-[30px] pb-[60px]">
				<HighlightedRecommendationsComponent
					propertyRecommendation={recommendations.highlightedPropertyRecommendation}
					ancilliaryRecommendation={recommendations.highlightedAncilliaryRecommendation}
					sectionTitle={t('Our Recommendations')}
				/>
				<RecommendationsListComponent
					sectionTitle={t('Other destinations you might enjoy')}
					recommendations={recommendations.propertiesRecommendations}
					redirectLink={localeLink(locale ?? 'en')('/find_a_hotel_or_resort/')}
					placement="for_you-other_destinations"
				/>
				<RecommendationsListComponent
					sectionTitle={t('Guest Favourites')}
					recommendations={recommendations.ancilliariesRecommendations?.recommendations}
					mainContainerClassNames="pb-[60px]"
					redirectLink={recommendations.ancilliariesRecommendations?.url}
					hideLocation={!!globalStore?.currentProperty}
					placement="for_you-guest_favourites"
				/>
			</div>
		</main>
	);
});
